import { useEffect, useState } from "react";
import { hrefOf, type AgencyHome, type AgencySession } from "@nabla/api";
import { agencyRead, type AgencyRead } from "../client/agency.ts";
import { Loads } from "../client/index.ts";
import { Empty, PageHeader } from "../design/primitives/Page.tsx";
import { Rows } from "../design/primitives/List.tsx";
import { Section } from "../design/primitives/Section.tsx";
import { Skeleton } from "../design/primitives/Skeleton.tsx";
import { stageWords, titleText } from "../features/change/words.ts";
import { overviewCopy } from "../features/overview/copy.ts";
import { Failed } from "./App.tsx";
import { ClientCard } from "./ClientCard.tsx";
import { agencyCopy as t } from "./copy.ts";
import { goTo, intoClient } from "./navigation.ts";
import styles from "./agency.module.css";

export function Home({ session }: { readonly session: AgencySession }) {
  const [home, setHome] = useState<AgencyRead<AgencyHome> | undefined>(undefined);
  const load = () => void agencyRead.inicio().then(setHome);
  useEffect(load, []);
  const only = session.clients.length === 1 ? session.clients[0] : undefined;
  useEffect(() => {
    if (only !== undefined) goTo(intoClient(only.id));
  }, [only]);
  const header = <PageHeader title={session.agency.name} intro={t.inicio.intro} />;
  const pending = session.pending === 0 ? null : <p className={styles.small} role="status">{t.inicio.liberando(session.pending)}</p>;
  if (session.clients.length === 0) return <>{header}{pending ?? <Empty>{t.inicio.nenhum}</Empty>}</>;
  if (home === undefined || only !== undefined) return <>{header}<div aria-busy="true"><Skeleton height="240px" /></div></>;
  if (!home.ok) return <Failed onRetry={load} />;
  const now = Date.now();
  const { waiting, clients } = home.value;
  return (
    <>
      {header}
      {pending}
      <div className={styles.blocks}>
        <Section title={overviewCopy.precisam.titulo} {...(waiting.length === 0 ? {} : { count: String(waiting.length) })}>
          {waiting.length === 0 ? <Empty>{t.inicio.nadaEsperando}</Empty> : (
            <Rows>
              {waiting.map(({ client, change }) => (
                <li key={`${client.id}:${change.id}`} className={styles.need}>
                  <Loads>
                    {() => (
                      <>
                        <a href={intoClient(client.id, hrefOf("mudanca", change.id))}>{titleText(change.title)}</a>
                        <span className={styles.meta}>{`${client.name} · ${stageWords(change, undefined, now).label}`}</span>
                      </>
                    )}
                  </Loads>
                </li>
              ))}
            </Rows>
          )}
        </Section>
        <Section title={t.lugares.clientes} count={String(clients.length)}>
          <div className={styles.cards}>
            {clients.map((client) => <ClientCard key={client.id} id={client.id} name={client.name} card={client.card} now={now} />)}
          </div>
        </Section>
      </div>
    </>
  );
}
